// The priced desk view of one option: header totals, the option tabs and
// settings, then the Take Off (rates block + the template's table), the
// Summary, the REVIEW or a maintenance option's Yearly Summaries (#515). Edits save through
// options.ts; the page does the loading.
import { useEffect, type ReactNode } from 'react';
import { countWaiting } from '../lib/device-db';
import { useDeviceQuery } from '../lib/hooks';
import { refreshJob } from '../lib/jobs';
import type { DeviceRecord } from '../lib/device-db';
import { canRecommend, recommendOption, removeOption, saveFields, totalsToSave } from '../lib/options';
import { ApiError, isConnectionFailure } from '../lib/api';
import type { OptionKind, OptionScope } from '@higgins/m2q-pricing';
import { areasMissingReason, areasToChooseAgain, isFrozenVersion, jobPricesOf, type SectionInfo } from '@higgins/m2q-pricing';
import type { PricedOption, PricingRates, SectionInput } from '@higgins/m2q-pricing';
import { priceBlockers, type ReviewAnswers } from '@higgins/m2q-pricing';
import type { PricingReferenceFromApi } from '../lib/types';
import { AcceptPanel } from './accept-panel';
import { ProposalPanel } from './proposal-panel';
import { TakeoffExportPanel } from './takeoff-export-panel';
import { ReviewPackPanel } from './review-pack-panel';
import { OptionBar } from './option-bar';
import { ReviewTab } from './review-tab';
import { JobPricesNotice } from './job-prices-notice';
import { SummaryTab } from './summary-tab';
import { HoursPriced } from './hours-priced';
import { advanceMeasure } from '../lib/measure-status-save';
import { formatMoney, formatPercent, formatSell } from './surface-format';
import { OptionLockBanner } from './option-lock-banner';
import { VersionPanel } from './version-panel';
import { Notice } from './message';
import { formatDate } from '../lib/dates';
import { answersOf, refusedAnswersOf } from '../lib/review-answers';
import { saveReviewAnswer } from '../lib/review-records';
import { maintenanceFigures, maintenanceProposalAbout, maintenanceProposalBlocker, reviewQuestionsFor, type MaintenancePricing } from '../lib/maintenance-pricing';
import { ContractTotals, YearlySummaryTab } from './yearly-summary-tab';
import { YearLinesPreview } from './year-lines-preview';
import { ScopePaTab } from './scope-pa-tab';
import { YearlyPdfPanel } from './yearly-pdf-panel';
import { contractHeader } from './maintenance-header';
import { TakeOffSheet } from './take-off-sheet';


export type DeskTab = 'take-off' | 'summary' | 'review' | 'yearly' | 'scope-pa';
const TABS: { key: DeskTab; label: string }[] = [
  { key: 'take-off', label: 'Take Off' },
  { key: 'summary', label: 'Summary' },
  { key: 'review', label: 'REVIEW' },
];
/** A maintenance option's extra sheets (#515): the Yearly Summaries, then Scope PA (MJ, the template's Scope sheet). */
const MAINTENANCE_TABS: { key: DeskTab; label: string }[] = [
  { key: 'yearly', label: 'Yearly Summaries' },
  { key: 'scope-pa', label: 'Scope PA' },
];

/** Why the shown option can't be accepted yet: the REVIEW, or changes the server hasn't got (it prices what CRM holds). */
/** Documents are made from what CRM has: none while this device's changes are still on their way. */
function syncBlocker(option: DeviceRecord, waiting: number): string | null {
  if (option.etag === null || waiting > 0) return 'Waiting for your changes to reach CRM — they sync within a minute, or tap Sync now at the top of the page.';
  return null;
}

function acceptBlocker(blockers: string[], option: DeviceRecord, waiting: number): string | null {
  if (blockers.length) return 'Complete the REVIEW first.';
  return syncBlocker(option, waiting);
}

/** Remove the shown option, then show the first remaining one; returns why it couldn't be removed, or null. */
async function removeAndShowFirst(option: DeviceRecord, options: DeviceRecord[], show: (id: string) => void): Promise<string | null> {
  try {
    await removeOption(option);
    const next = options.find((o) => o.id !== option.id);
    if (next) show(next.id);
    return null;
  } catch (err) {
    if (isConnectionFailure(err)) return 'Removing an option needs signal.';
    return err instanceof ApiError ? err.message : 'The option could not be removed.';
  }
}

/** The version's review answers; old answers in a newer format can't be read here, so it is reported and left untouched. */
function readReview(version: DeviceRecord, records: readonly DeviceRecord[], questions: PricingReferenceFromApi['reviewQuestions']): { review: ReviewAnswers } | { error: string } {
  try {
    return { review: answersOf(version, records, questions) };
  } catch (err) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
}

const num = (value: unknown): number | null => (typeof value === 'number' ? value : null);
/** Each area's side, by area id: Scope PA and its PDF group areas Interior, then Exterior. */
const sidesOf = (info: Map<string, SectionInfo>) => new Map([...info].map(([id, i]) => [id, i.side]));

/** Summary and REVIEW keep the width they had before the page widened for the Take Off's columns (#441): their figures sit apart on a wide window. */
const SheetWidth = ({ children }: { children: ReactNode }) => <div className="max-w-7xl">{children}</div>;

export interface TakeOffViewProps {
  back: ReactNode;
  job: string;
  measure: DeviceRecord;
  records: DeviceRecord[];
  version: DeviceRecord;
  options: DeviceRecord[];
  option: DeviceRecord;
  onSelectOption: (id: string) => void;
  onAddOption: (kind: OptionKind) => void;
  tab: DeskTab;
  onTab: (tab: DeskTab) => void;
  reference: PricingReferenceFromApi;
  scope: OptionScope;
  labourType: string;
  rates: PricingRates;
  /** The rates without this option's own changes, for the rate boxes' placeholders. */
  baseRates: PricingRates;
  sections: SectionInput[];
  info: Map<string, SectionInfo>;
  priced: PricedOption;
  /** A maintenance option's contract (#515); null for a repaint; an error said plainly. */
  maintenance: MaintenancePricing | { error: string } | null;
}

export function TakeOffView(props: TakeOffViewProps) {
  const { back, job, measure, version, options, option, onSelectOption, onAddOption, tab, onTab, reference, labourType, info, priced } = props;
  const contract = props.maintenance && 'contract' in props.maintenance ? props.maintenance : null;
  const tabs = contract ? [...TABS, ...MAINTENANCE_TABS] : TABS;

  // Keep the totals CRM lists show in step with the price (only when they differ).
  useEffect(() => {
    const totals = totalsToSave(option, priced);
    if (totals) void saveFields(option, totals);
  }, [option, priced]);

  const waiting = useDeviceQuery(() => countWaiting(option.jobId), [option.jobId]) ?? 0;
  const frozen = isFrozenVersion(version.fields.hig_status);
  const read = readReview(version, props.records, reference.reviewQuestions);
  const blockers = 'review' in read ? priceBlockers(reference.reviewQuestions, read.review).map((q) => q.question) : ['the review (it could not be read)'];
  const unreadable = [...info.entries()].filter(([, i]) => i.error);
  // Other approved hours need a reason before anything is accepted, proposed or issued (the server refuses too).
  const noReason = areasMissingReason(info.values());
  // So does Assessed chosen before the PM's hours were fixed per surface (#607).
  const chooseAgain = areasToChooseAgain(info.values());
  const reasonBlock = noReason.length
    ? `Give a reason for the Other hours on the REVIEW first (${noReason.join(', ')}).`
    : chooseAgain.length
      ? `Choose Assessed again on the REVIEW first (${chooseAgain.join(', ')}).`
      : null;
  const unpricedHire = [...info.values()].flatMap((i) => i.unpricedHire.map((item) => `${item} (${i.name})`));
  const noAbseilRate = !reference.labourRates.find((r) => r.labourType === labourType)?.abseil && props.sections.some((s) => (s.abseil ?? 0) > 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div>
          {back}
          {/* The desk is "Pricing"; its tabs keep the STM002 sheet names (#637). */}
          <h2 className="mt-1 text-base font-semibold text-slate-900">
            {job} · Pricing <span className="font-normal text-slate-500">· {String(measure.fields.hig_name ?? '')} · {String(version.fields.hig_name ?? '')} {String(version.fields.hig_status ?? 'Draft')}</span>
          </h2>
          <p className="text-xs text-slate-500">{reference.branch.name} · rates as at {formatDate(reference.date)} (the version&rsquo;s pricing date)</p>
        </div>
        <dl className="flex gap-5 text-right">
          {(contract ? contractHeader(contract, priced.sell, blockers.length === 0) : [['Total Sell', formatSell(priced.sell, blockers.length === 0)], ['Total Cost', formatMoney(priced.cost)], ['GP%', formatPercent(priced.gp)]]).map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-slate-500">{label}</dt>
              <dd className="text-lg font-semibold tabular-nums text-slate-900">{value}</dd>
            </div>
          ))}
        </dl>
      </div>

      <OptionBar
        options={options.map((o) => ({ id: o.id, name: String(o.fields.hig_name ?? ''), recommended: o.fields.hig_recommended === true, accepted: o.fields.hig_accepted === true }))}
        selectedId={option.id}
        onSelect={onSelectOption}
        onAdd={frozen ? null : onAddOption}
        onRecommend={!frozen && canRecommend(option) ? () => void recommendOption(version, options, option) : null}
        onRemove={!frozen && options.length > 1 && option.etag !== null ? () => removeAndShowFirst(option, options, onSelectOption) : null}
        name={String(option.fields.hig_name ?? '')}
        labourType={labourType}
        markupMaterials={num(option.fields.hig_markupmaterials)}
        markupOther={num(option.fields.hig_markupother)}
        negotiatingFactor={num(option.fields.hig_negotiatingfactor)}
        defaults={{ markupMaterials: reference.settings['markup.materials'], markupOther: reference.settings['markup.other'] }}
        onChange={(fields) => saveFields(option, fields)}
        locked={option.fields.hig_accepted === true || frozen}
      />
      <OptionLockBanner option={option} onUnlocked={() => void refreshJob(job)} />
      <VersionPanel measure={measure} version={version} options={options} records={props.records} issueBlockers={[...blockers, ...(noReason.length ? [`a reason for the Other hours (${noReason.join(', ')})`] : []), ...(chooseAgain.length ? [`Assessed chosen again (${chooseAgain.join(', ')})`] : []), ...(waiting ? ['changes are still syncing'] : [])]} onChanged={() => void refreshJob(job)} />

      <div className="flex gap-1 border-b border-slate-200" role="tablist" aria-label="Sheets">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => onTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-1.5 text-sm outdoor:min-h-11 ${tab === t.key ? 'border-primary font-medium text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {option.state === 'conflict' && !option.refusedReason && <Notice>This option was changed somewhere else since it was loaded; your change wasn&rsquo;t saved over it. Reopen the job to see the latest.</Notice>}
      {unreadable.map(([id, i]) => (
        <Notice key={id}>One area is left out of the price: {i.error}</Notice>
      ))}
      {unpricedHire.length > 0 && <Notice>Hire with no rate on {formatDate(reference.date)}, left out of the price until it&rsquo;s replaced or given a typed day rate: {unpricedHire.join('; ')}.</Notice>}
      {noAbseilRate && <Notice>An area has abseil hours but {reference.branch.name} has no abseil rate in CRM, so abseil is priced at nothing.</Notice>}

      {'error' in read && <Notice>{read.error}</Notice>}
      {props.maintenance && 'error' in props.maintenance && <Notice>The maintenance contract can&rsquo;t be priced: {props.maintenance.error}</Notice>}
      {tab === 'yearly' && contract && (
        <div className="space-y-2">
          <YearlyPdfPanel jobNumber={job} optionName={String(option.fields.hig_name ?? '')} version={version} pricing={contract} sides={sidesOf(info)} priceShown={blockers.length === 0} />
          <YearlySummaryTab pricing={contract} priceShown={blockers.length === 0} />
        </div>
      )}
      {tab === 'scope-pa' && contract && <ScopePaTab pricing={contract} sides={sidesOf(info)} priceShown={blockers.length === 0} />}
      {tab === 'summary' && (
        <SheetWidth>
          <SummaryTab
            option={priced}
            info={info}
            gpFloor={reference.settings['gp.floor']}
            blockers={blockers}
            accept={
              <>
                {contract && <ContractTotals pricing={contract} repaintSell={priced.sell} priceShown={blockers.length === 0} />}
                <ProposalPanel
                  optionId={option.id}
                  blockedBecause={(contract && maintenanceProposalBlocker(contract)) || reasonBlock || acceptBlocker(blockers, option, waiting)}
                  about={contract ? maintenanceProposalAbout(contract) : undefined}
                />
                <ReviewPackPanel optionId={option.id} blockedBecause={syncBlocker(option, waiting)} />
                <TakeoffExportPanel optionId={option.id} blockedBecause={syncBlocker(option, waiting)} />
                <AcceptPanel
                  optionId={option.id}
                  optionName={String(option.fields.hig_name ?? '')}
                  acceptedOn={option.fields.hig_accepted === true ? formatDate(option.fields.hig_acceptedon) ?? 'an earlier day' : null}
                  blockedBecause={reasonBlock || acceptBlocker(blockers, option, waiting)}
                  sell={contract ? contract.yearly.sell : priced.sell}
                  preview={contract && <YearLinesPreview pricing={contract} priced={priced} rates={props.rates} labourType={labourType} />}
                  onAccepted={() => void refreshJob(job)}
                />
              </>
            }
          />
        </SheetWidth>
      )}
      {tab === 'review' && 'review' in read && (
        <SheetWidth>
          <JobPricesNotice rates={props.rates} prices={jobPricesOf(props.records, props.measure.id)} />
          <fieldset disabled={frozen} className="contents">
          <HoursPriced info={info} records={props.records} measure={measure} frozen={frozen} />
          <ReviewTab
            questions={reviewQuestionsFor(reference.reviewQuestions, options)}
            figures={contract ? maintenanceFigures(contract) : undefined}
            answers={read.review}
            onChange={async (question, change) => {
              await saveReviewAnswer(version, question, read.review.get(question.key), change);
              // Reviewing moves the measure on to In review (never back; measure-status.ts).
              await advanceMeasure(measure, 'In review');
            }}
            sections={props.sections}
            priced={priced}
            notSaved={refusedAnswersOf(props.records, version.id)}
          />
          </fieldset>
        </SheetWidth>
      )}
      {tab === 'take-off' && (
        <fieldset disabled={option.fields.hig_accepted === true || frozen} className="contents">
          <TakeOffSheet {...props} priceShown={blockers.length === 0} />
        </fieldset>
      )}
    </div>
  );
}
